
import React, { useEffect, useState } from 'react';
import { db } from '../../../firebase';
import { CompanySettingModel, BranchModel, MemberModel, PolicySettingModel, GRADE_TYPES, CompanyPhone, CompanyKakaoUrl } from '../../../models';
import { collection, onSnapshot, query, orderBy, doc, getDoc, addDoc } from 'firebase/firestore';
import { X, Phone } from 'lucide-react';

import LandingHeader, { LandingView } from './landingHeader';
import LandingFooter from './landingFooter';
import IntroHome from './introHome';
import IntroAcademy from './introAcademy';
import IntroStudySystem from './introStudySystem';
import IntroAcademyLife from './introAcademyLife';
import IntroTeachers from './introTeachers';
import IntroSuccessStories from './introSuccessStories';
import IntroReviews from './introReviews';
import IntroNotice from './introNotice';
import { subscribeTeachers } from './introTeachers';

interface LandingProps {
    onStart: () => void;
    memberProfile?: MemberModel | null;
    onLogout?: () => void;
    onNavigate?: (view: string) => void;
}

const Landing: React.FC<LandingProps> = ({ onStart, memberProfile, onLogout, onNavigate }) => {
    const [companyInfo, setCompanyInfo] = useState<CompanySettingModel | null>(null);
    const [policySettings, setPolicySettings] = useState<PolicySettingModel | null>(null);
    const [activeView, setActiveView] = useState<LandingView>('home');
    const [showInquiryModal, setShowInquiryModal] = useState(false);
    const [branches, setBranches] = useState<BranchModel[]>([]);

    useEffect(() => {
        const fetchCompanyInfo = async () => {
            const docRef = doc(db, 'settings', 'company');
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                setCompanyInfo(docSnap.data() as CompanySettingModel);
            }

            const policyRef = doc(db, 'settings', 'policy');
            const policySnap = await getDoc(policyRef);
            if (policySnap.exists()) {
                setPolicySettings(policySnap.data() as PolicySettingModel);
            }
        };
        fetchCompanyInfo();

        const branchesQuery = query(collection(db, 'branches'), orderBy('createdAt', 'desc'));
        const unsubscribeBranches = onSnapshot(branchesQuery, (snapshot) => {
            const list: BranchModel[] = [];
            snapshot.forEach((doc) => list.push({ id: doc.id, ...doc.data() } as BranchModel));
            setBranches(list);
        });

        // Background preload teacher images as soon as landing loads
        const unsubscribeTeachers = subscribeTeachers(() => {});

        return () => {
            unsubscribeBranches();
            unsubscribeTeachers();
        };
    }, []);

    const InquiryModal: React.FC<{ branches: BranchModel[], onClose: () => void }> = ({ branches, onClose }) => {
        const [form, setForm] = useState({
            type: 'student' as 'student' | 'parent',
            studentName: '',
            grade: '',
            school: '',
            phone: '',
            content: '',
            branchId: 'main'
        });
        const [submitting, setSubmitting] = useState(false);

        const handleSubmit = async (e: React.FormEvent) => {
            e.preventDefault();
            setSubmitting(true);
            try {
                await addDoc(collection(db, 'inquiries'), {
                    ...form,
                    status: 'new',
                    createdAt: Date.now()
                });
                alert('상담 신청이 완료되었습니다. 조속히 연락드리겠습니다.');
                onClose();
            } catch (error) {
                console.error('Error submitting inquiry:', error);
                alert('오류가 발생했습니다. 다시 시도해주세요.');
            } finally {
                setSubmitting(false);
            }
        };

        return (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-0 sm:p-4">
                <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300" onClick={onClose}></div>
                <div className="relative w-full h-full sm:h-auto sm:max-w-lg bg-white rounded-none sm:rounded-[2.5rem] shadow-2xl p-6 sm:p-8 md:p-10 max-h-full sm:max-h-[85vh] overflow-y-auto animate-in sm:zoom-in-95 duration-300 selection:bg-indigo-100 selection:text-indigo-900">
                    <button onClick={onClose} className="absolute top-6 right-6 text-slate-400 hover:text-slate-600 transition-colors z-10">
                        <X size={24} />
                    </button>

                    <div className="space-y-6">
                        <div className="text-center space-y-2">
                            <h2 className="text-2xl font-black text-slate-900">상담신청</h2>
                            <p className="text-slate-500 font-medium text-sm">전문가와 함께 최적의 학습 로드맵을 그려보세요.</p>
                            <p className="text-indigo-600 font-black text-[11px] bg-indigo-50 px-3 py-1.5 rounded-full inline-block mt-1">
                                📞 신청 시 기재하신 연락처로 순차적으로 전화 연락을 드립니다.
                            </p>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div className="flex p-1 bg-slate-100 rounded-2xl">
                                {(['student', 'parent'] as const).map((t) => (
                                    <button
                                        key={t}
                                        type="button"
                                        onClick={() => setForm({ ...form, type: t })}
                                        className={`flex-1 py-3 rounded-xl text-sm font-black transition-all ${form.type === t ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500'}`}
                                    >
                                        {t === 'student' ? '학생' : '학부모'}
                                    </button>
                                ))}
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <label className="text-xs font-black text-slate-500 uppercase tracking-widest pl-1">학생이름</label>
                                    <input
                                        required
                                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-sm font-medium bg-white text-slate-800"
                                        value={form.studentName}
                                        onChange={(e) => setForm({ ...form, studentName: e.target.value })}
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-xs font-black text-slate-500 uppercase tracking-widest pl-1">학년</label>
                                    <select
                                        required
                                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-sm font-medium bg-white text-slate-800"
                                        value={form.grade}
                                        onChange={(e) => setForm({ ...form, grade: e.target.value })}
                                    >
                                        <option value="">선택</option>
                                        {GRADE_TYPES.map((g) => (
                                            <option key={g} value={g}>{g}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="space-y-1.5">
                                <label className="text-xs font-black text-slate-500 uppercase tracking-widest pl-1">학교</label>
                                <input
                                    required
                                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-sm font-medium bg-white text-slate-800"
                                    value={form.school}
                                    onChange={(e) => setForm({ ...form, school: e.target.value })}
                                />
                            </div>

                            <div className="space-y-1.5">
                                <label className="text-xs font-black text-slate-500 uppercase tracking-widest pl-1">연락처</label>
                                <input
                                    required
                                    type="tel"
                                    inputMode="numeric"
                                    maxLength={11}
                                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-sm font-medium bg-white text-slate-800"
                                    placeholder="숫자만 입력 (예: 01012345678)"
                                    value={form.phone}
                                    onChange={(e) => {
                                        const val = e.target.value.replace(/[^0-9]/g, '');
                                        if (val.length <= 11) setForm({ ...form, phone: val });
                                    }}
                                />
                            </div>

                            <div className="space-y-1.5">
                                <label className="text-xs font-black text-slate-500 uppercase tracking-widest pl-1">문의사항</label>
                                <textarea
                                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-sm font-medium bg-white text-slate-800 h-32 resize-none"
                                    placeholder="궁금하신 내용을 입력해주세요."
                                    value={form.content}
                                    onChange={(e) => setForm({ ...form, content: e.target.value })}
                                />
                            </div>



                            <button
                                type="submit"
                                disabled={submitting}
                                className="w-full bg-indigo-600 text-white py-4 rounded-2xl font-black text-lg shadow-xl shadow-indigo-100 hover:bg-indigo-700 transition-all active:scale-95 disabled:opacity-50"
                            >
                                {submitting ? '신청 중...' : '상담 신청하기'}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        );
    };

    const renderContent = () => {
        switch (activeView) {
            case 'home':
                return <IntroHome onStart={onStart} onShowInquiry={() => setShowInquiryModal(true)} />;
            case 'about':
                return <IntroAcademy />;
            case 'study-system':
                return <IntroStudySystem />;
            case 'teachers-intro':
                return <IntroTeachers />;
            case 'academy-life':
                return <IntroAcademyLife />;
            case 'notice':
                return <IntroNotice />;
            case 'success-stories':
                return <IntroSuccessStories />;
            case 'reviews':
                return <IntroReviews />;
            default:
                return <IntroHome onStart={onStart} onShowInquiry={() => setShowInquiryModal(true)} />;
        }
    };

    return (
        <div className="min-h-screen bg-[#0B132B] text-slate-100 flex flex-col font-sans selection:bg-indigo-500/30 selection:text-indigo-200">
            <LandingHeader
                companyInfo={companyInfo}
                activeView={activeView}
                setActiveView={setActiveView}
                memberProfile={memberProfile}
                onStart={onStart}
                onLogout={onLogout}
                onNavigate={onNavigate}
                onShowInquiry={() => setShowInquiryModal(true)}
            />

            <main className="flex-1">
                {renderContent()}
            </main>

            {showInquiryModal && (
                <InquiryModal branches={branches} onClose={() => setShowInquiryModal(false)} />
            )}

            <LandingFooter companyInfo={companyInfo} policySettings={policySettings} onNavigate={onNavigate} onStart={onStart} />

            {/* Floating Quick Action Buttons: KakaoTalk & Direct Phone */}
            <div className="fixed bottom-6 right-6 z-50 flex flex-col items-center gap-3">
                {/* KakaoTalk Button */}
                <a
                    href={companyInfo?.kakaoUrl || CompanyKakaoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="카카오톡 상담하기"
                    className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#FEE500] hover:bg-[#FADA0A] text-[#191919] shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110 active:scale-95 cursor-pointer border border-yellow-400/40 group"
                >
                    <svg className="w-6 h-6 sm:w-7 sm:h-7 fill-current" viewBox="0 0 24 24">
                        <path d="M12 3C6.477 3 2 6.477 2 10.767c0 2.766 1.83 5.19 4.605 6.558l-1.17 4.298c-.104.385.342.695.674.468l5.123-3.41c.254.02.51.035.768.035 5.523 0 10-3.477 10-7.767S17.523 3 12 3z"/>
                    </svg>
                </a>

                {/* Direct Phone Call Button */}
                <a
                    href={`tel:${companyInfo?.phone || CompanyPhone}`}
                    title="전화 문의하기"
                    className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#25D366] hover:bg-[#20bd5a] text-white shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110 active:scale-95 cursor-pointer border border-emerald-400/40 group"
                >
                    <Phone size={24} className="fill-current stroke-none" />
                </a>
            </div>
        </div>
    );
};

export default Landing;
